/**
 * Nomination types and enums for the Award Nomination System
 * Requirements: 1.1, 2.1, 3.2
 */

/**
 * Nomination role enumeration with hierarchy levels
 * Requirements: 1.1 - Role-based access control
 */
export enum NominationRole {
  EMPLOYEE = 'Employee',
  MANAGER = 'Manager',
  REVIEWER = 'Reviewer',
  ADMIN = 'Admin'
}

/**
 * Award type enumeration
 * Requirements: 2.1 - Award type selection
 */
export enum AwardType {
  SPOT = 'SPOT Award',
  QUARTERLY = 'Quarterly Excellence Award',
}

/**
 * Whether one person is being recognised, or a team.
 *
 * ── Why this is NOT called awardType ──────────────────────────────────────────────────────
 *
 * The screen labels it "Select Award Type", and `awardType` was already taken — it holds SPOT
 * Award or Quarterly Excellence Award and is read by the feed, the review queue, the emails and
 * both award forms. Two different questions cannot share one field, and renaming the existing one
 * would rewrite every stored row. So the label reads as asked and the field is named for what it
 * actually distinguishes.
 *
 * ── Absent means Individual ───────────────────────────────────────────────────────────────
 *
 * Every nomination stored before this existed is one person, and that is what an unset value
 * means. Readers default rather than treating it as unknown, so no migration is needed and an
 * old row never renders as a team with no members.
 */
export enum NominationKind {
  INDIVIDUAL = 'Individual',
  TEAM = 'Team',
}

/**
 * One member of a nominated team.
 *
 * `id` is the employee's email, which is what the individual path already stores in nomineeId and
 * what every other per-employee store in this application is keyed by.
 */
export interface TeamMember {
  id: string;
  name: string;
}

/**
 * Nomination workflow status enumeration
 * Requirements: 2.2, 2.5, 3.3, 3.4, 4.1
 */
export enum NominationStatus {
  DRAFT = 'Draft',
  SUBMITTED = 'Submitted',
  UNDER_REVIEW = 'Under Review',
  APPROVED = 'Approved',
  REJECTED = 'Rejected',
  PUBLISHED = 'Published'
}

/**
 * Main nomination interface
 * Requirements: 2.1 - Nomination data capture
 */
export interface Nomination {
  id: string;
  /**
   * For an individual, the nominee's email. For a team, a derived `team:<slug>` identifier.
   *
   * Populated for BOTH kinds on purpose. Every existing reader — the feed, the cards, the review
   * queue, the admin table, the emails, the serializer's required-field check — goes through
   * nomineeId/nomineeName, and leaving them empty for a team would empty all of those screens
   * rather than teaching them about teams.
   */
  nomineeId: string;
  /** The person's name, or the team's. See nomineeId for why both kinds fill this in. */
  nomineeName: string;
  nominatorId: string;
  nominatorName: string;
  awardType: AwardType;
  /** Individual or Team. Absent on every nomination stored before teams existed — see the enum. */
  nominationType?: NominationKind;
  /** Team nominations only. Also copied into nomineeName, which is what existing screens read. */
  teamName?: string;
  /** Team nominations only. At least one member; duplicates are rejected at selection time. */
  teamMembers?: TeamMember[];
  reason: string;
  supportingDetails?: string;
  nomineePhotoUrl?: string;
  status: NominationStatus;
  rejectionReason?: string;
  reviewerNotes: ReviewerNote[];
  feedComments?: FeedComment[];
  publishedAt?: string;
  publishedBy?: string;
  /*
   * Who approved it, and when.
   *
   * The approver was already known at the moment of the write -- handleApprove passes the
   * signed-in email to updateNomination -- and was being used only for the audit entry and
   * then discarded, so the Ready to Publish queue could say a nomination was approved but not
   * by whom.
   *
   * Optional, and absent on every nomination approved before this was recorded. The queue
   * shows the attribution only when it is actually known: inferring it from the audit trail
   * would mean guessing from a details string that records which FIELDS changed and not what
   * they changed to, and a wrong name against an approval is worse than no name.
   */
  approvedAt?: string;
  approvedBy?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Reviewer note interface
 * Requirements: 3.2 - Reviewer feedback
 */
export interface ReviewerNote {
  id: string;
  nominationId: string;
  reviewerId: string;
  reviewerName: string;
  content: string;
  createdAt: string;
}

/**
 * Audit entry interface for admin actions
 * Requirements: 6.2, 6.3 - Audit trail
 */
export interface AuditEntry {
  id: string;
  nominationId: string;
  action: string;
  performedBy: string;
  performedByName: string;
  details: string;
  timestamp: string;
}

/**
 * Input interface for creating a nomination
 * Requirements: 2.1 - Nomination creation
 */
export interface CreateNominationInput {
  nomineeId: string;
  nomineeName: string;
  awardType: AwardType;
  /** Defaults to Individual when omitted, so every existing caller keeps working unchanged. */
  nominationType?: NominationKind;
  teamName?: string;
  teamMembers?: TeamMember[];
  reason: string;
  supportingDetails?: string;
  nomineePhotoUrl?: string;
}

/**
 * Input interface for updating a nomination
 * Requirements: 6.2 - Admin edit
 */
export interface UpdateNominationInput {
  nomineeId?: string;
  nomineeName?: string;
  awardType?: AwardType;
  nominationType?: NominationKind;
  teamName?: string;
  teamMembers?: TeamMember[];
  reason?: string;
  supportingDetails?: string;
  status?: NominationStatus;
  rejectionReason?: string;
  publishedAt?: string;
  publishedBy?: string;
  approvedAt?: string;
  approvedBy?: string;
}

/**
 * Input interface for adding a reviewer note
 * Requirements: 3.2 - Reviewer notes
 */
export interface ReviewerNoteInput {
  nominationId: string;
  content: string;
}

/**
 * Feed comment interface for social-style comments on published nominations
 */
export interface FeedComment {
  id: string;
  nominationId: string;
  authorId: string;
  authorName: string;
  content: string;
  createdAt: string;
}

/**
 * Input interface for creating a feed comment
 */
export interface CreateFeedCommentInput {
  nominationId: string;
  content: string;
}
